import type { Key, SortModel } from '@thinkalpha/table-client';
import { compareKey } from '@thinkalpha/table-client';
import { isEqual } from 'es-toolkit';
import type { TableStatus } from 'src/components/table-view/model';

export interface TableV2TableRunInput {
    readonly filter: string | undefined;
    readonly sort: SortModel;
    readonly tableKey: Key;
    readonly userViewKey: string;
}

export interface TableV2TableRun extends TableV2TableRunInput {
    readonly generation: number;
}

export interface TableV2TableRunObservation {
    readonly connectionStatus: TableStatus;
    readonly error: Error | undefined;
    readonly failureCount: number;
}

export type TableV2WidgetRuntimeState =
    | { readonly type: 'uninitialized'; readonly generation: number }
    | { readonly type: 'loading'; readonly phase: 'queued' | 'running'; readonly run: TableV2TableRun }
    | { readonly type: 'error'; readonly error: Error; readonly run: TableV2TableRun }
    | { readonly type: 'ready'; readonly run: TableV2TableRun };

export type TableV2WidgetRuntimeAction =
    | { readonly type: 'configureTableRun'; readonly input: TableV2TableRunInput | null }
    | {
          readonly type: 'observeTableRun';
          readonly generation: number;
          readonly observation: TableV2TableRunObservation;
      };

export function createInitialTableV2WidgetRuntimeState(): TableV2WidgetRuntimeState {
    return { type: 'uninitialized', generation: 0 };
}

function getGeneration(state: TableV2WidgetRuntimeState): number {
    return state.type === 'uninitialized' ? state.generation : state.run.generation;
}

function hasSameInput(run: TableV2TableRun, input: TableV2TableRunInput): boolean {
    return (
        compareKey(run.tableKey, input.tableKey) &&
        run.filter === input.filter &&
        isEqual(run.sort, input.sort) &&
        run.userViewKey === input.userViewKey
    );
}

function makeRun(input: TableV2TableRunInput, generation: number): TableV2TableRun {
    return {
        filter: input.filter,
        generation,
        sort: { ...input.sort },
        tableKey: { ...input.tableKey },
        userViewKey: input.userViewKey,
    };
}

export function tableV2WidgetRuntimeReducer(
    state: TableV2WidgetRuntimeState,
    action: TableV2WidgetRuntimeAction,
): TableV2WidgetRuntimeState {
    switch (action.type) {
        case 'configureTableRun': {
            if (action.input === null) {
                if (state.type === 'uninitialized') return state;
                return { type: 'uninitialized', generation: getGeneration(state) };
            }

            if (state.type !== 'uninitialized' && hasSameInput(state.run, action.input)) return state;

            return {
                type: 'loading',
                phase: 'queued',
                run: makeRun(action.input, getGeneration(state) + 1),
            };
        }
        case 'observeTableRun': {
            if (state.type === 'uninitialized' || state.run.generation !== action.generation) return state;

            const { connectionStatus, error, failureCount } = action.observation;
            if (failureCount > 0) {
                if (!error || (state.type === 'loading' && state.phase === 'queued')) return state;
                if (state.type === 'error' && state.error === error) return state;
                return { type: 'error', error, run: state.run };
            }

            if (connectionStatus === 'connected') {
                if (state.type === 'ready') return state;
                return { type: 'ready', run: state.run };
            }

            if (state.type === 'loading' && state.phase === 'running') return state;
            return { type: 'loading', phase: 'running', run: state.run };
        }
        default: {
            const _exhaustive: never = action;
            return _exhaustive;
        }
    }
}
